import React, { memo, useRef, useState, useEffect, useCallback } from "react";
import "./CartRightSidebar.css";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import PosCustomIcon from "../../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import useGetDevice from "../../../hooks/useGetDevice";
import { SIDEBAR_CONFIG } from "./cartRightSidebarConfig";
import { CircularProgress } from "@mui/material";

const CartRightSidebar = ({ presetType, buttonHandlers }) => {
  const isMobileView = useGetDevice();

  const containerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const button = SIDEBAR_CONFIG[presetType] || [];

  const checkScrollable = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      const { scrollHeight, clientHeight, scrollTop } = el;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
      setShowScrollIndicator(isScrollable && !isAtBottom);
    }
  }, []);

  useEffect(() => {
    checkScrollable();
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollable);
      window.addEventListener("resize", checkScrollable);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
    };
  }, [checkScrollable]);

  // Re-check when buttons change (visibility toggling can change scrollability)
  useEffect(() => {
    checkScrollable();
  }, [buttonHandlers, checkScrollable]);

  const handleScrollDown = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        top: 200,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="rightSidebarWrapper">
      <div className="rightSidebarContainer" ref={containerRef}>
        {button?.map((btn) => {
          const state = buttonHandlers?.[btn.id] || {};

          const isLoading = state?.loading;
          const isDisabled = state?.disabled;
          const isActivated = state?.activated;
          const isVisible = state?.visible !== false;
          const isCustomerAdded = state?.customerAdded;

          if (!isVisible) return null;

          const Icon = isActivated && btn?.activatedIcon ? btn?.activatedIcon : btn?.icon;
          const iconIsComponent = typeof Icon === "function";
          const iconIsElement = React.isValidElement(Icon);

          const iconColor = isActivated
            ? btn?.activatedIconColor || btn?.iconColor || "#62748e"
            : isCustomerAdded
            ? "#15803d"
            : btn?.iconColor || "#62748e";

          const iconBg = isActivated
            ? btn?.activatedIconBg || "var(--surface-white)"
            : isCustomerAdded
            ? "rgba(22,163,74,0.12)"
            : btn?.iconBg || "#f3f4f6";

          return (
            <div
              key={btn?.id}
              className={`rightSidebarButtonContainer
                ${isDisabled ? "disabled" : ""}
                ${isActivated ? "activated" : ""}
                ${isCustomerAdded ? "customerAdded" : ""}
                `}
              style={{
                "--btn-icon-bg": btn?.iconBg || "#f3f4f6",
                "--btn-icon-color": btn?.iconColor || "#62748e",
                "--btn-activated-bg": btn?.activatedIconBg || btn?.iconBg || "#f3f4f6",
              }}
              onClick={
                !isLoading &&
                (!isDisabled ||
                  state?.allowClickWhenDisabled ||
                  btn?.id === "discount" ||
                  btn?.id === "offer")
                  ? state?.onClick
                  : undefined
              }
            >
              {isLoading ? (
                <CircularProgress size={"20px"} sx={{ color: "var(--surface-black)" }} />
              ) : (
                <>
                  {/* Icon with optional status dot */}
                  <div className="rsb-icon-wrap">
                    {btn?.image || (Icon && !iconIsComponent && !iconIsElement) ? (
                      <div className="custom-icon-bar" style={{ background: iconBg }}>
                        <PosCustomIcon image={btn?.image || Icon} height={"16px"} width={"16px"} />
                      </div>
                    ) : iconIsElement ? (
                      <div
                        className="custom-icon-bar"
                        style={{
                          color: iconColor,
                          background: iconBg,
                          height: "36px",
                          width: "36px",
                        }}
                      >
                        {Icon}
                      </div>
                    ) : iconIsComponent ? (
                      <div className="custom-icon-bar" style={{ background: iconBg }}>
                        <PosCustomIcon
                          icon={<Icon />}
                          height={"25px"}
                          width={"25px"}
                          color={iconColor}
                        />
                      </div>
                    ) : null}

                    {/* Status dot — green when customer added, accent colour when activated */}
                    {(isCustomerAdded || isActivated) && (
                      <span
                        className="rsb-status-dot"
                        style={{
                          background: isCustomerAdded
                            ? "#16a34a"
                            : btn?.activatedIconColor || iconColor,
                        }}
                      />
                    )}
                  </div>

                  <p
                    className={`rightSidebarButtonText ${isActivated ? "activated" : ""} ${
                      isCustomerAdded ? "customerAddedText" : ""
                    }`}
                  >
                    {isActivated && btn?.activatedLabel ? btn.activatedLabel : btn?.label}
                    {!isMobileView && btn?.shortcut && !isCustomerAdded && (
                      <>
                        <br />
                        <span className="rightSidebarShortcutText">({btn?.shortcut})</span>
                      </>
                    )}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="scrollIndicator" onClick={handleScrollDown}>
          <ChevronDownIcon style={{ width: "16px", height: "16px" }} />
        </div>
      )}
    </div>
  );
};

export default memo(CartRightSidebar);
